import { useState, useEffect, useCallback } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import { cn } from "@/lib/utils";

/**
 * PublicNav — Marketing site navigation bar
 *
 * - Sticky top, transparent on hero -> white with border on scroll
 * - Logo wordmark on the left
 * - Navigation links center/right
 * - CTA "Launch Agent" far right
 * - Mobile: hamburger -> full-screen overlay
 */

interface NavLink {
  label: string;
  href: string;
}

const navLinks: NavLink[] = [
  { label: "Features", href: "/features" },
  { label: "How it works", href: "/how-it-works" },
  { label: "Pricing", href: "/pricing" },
  { label: "Developers", href: "/developers" },
  { label: "Security", href: "/security" },
];

export function PublicNav() {
  const location = useLocation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 20);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    // Check initial scroll position
    handleScroll();
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  // Close mobile menu on route change
  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  const isActive = (href: string) => location.pathname === href;

  return (
    <>
      <header
        className={cn(
          "fixed top-0 left-0 right-0 z-50 transition-all duration-300",
          scrolled
            ? "glass-nav border-b border-outline-variant/30 shadow-sm"
            : "bg-transparent",
        )}
      >
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex items-center justify-between h-20">
            {/* Logo wordmark */}
            <Link to="/" className="shrink-0" aria-label="DeTrust home">
              <img
                src="/detrust-logo-primary.svg"
                alt="DeTrust AI"
                className="h-8 md:h-9 w-auto"
              />
            </Link>

            {/* Desktop nav links */}
            <nav
              className="hidden md:flex items-center gap-8"
              aria-label="Main navigation"
            >
              {navLinks.map((link) => (
                <Link
                  key={link.href}
                  to={link.href}
                  className={cn(
                    "text-body-sm font-medium transition-colors",
                    isActive(link.href)
                      ? "text-primary border-b-2 border-primary pb-1"
                      : "text-on-surface-variant hover:text-on-surface",
                  )}
                >
                  {link.label}
                </Link>
              ))}
            </nav>

            {/* Desktop CTA + Mobile hamburger */}
            <div className="flex items-center gap-4">
              {/* Desktop CTA */}
              <Link
                to="/chat"
                className="hidden md:inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
              >
                Launch Agent
                <span aria-hidden="true">&rarr;</span>
              </Link>

              {/* Mobile hamburger */}
              <button
                type="button"
                className="md:hidden p-2 -mr-2 text-foreground"
                onClick={() => setMobileOpen((prev) => !prev)}
                aria-label={mobileOpen ? "Close menu" : "Open menu"}
                aria-expanded={mobileOpen}
              >
                {mobileOpen ? (
                  <X className="h-6 w-6" />
                ) : (
                  <Menu className="h-6 w-6" />
                )}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-background flex flex-col pt-20"
          role="dialog"
          aria-modal="true"
          aria-label="Mobile navigation"
        >
          <nav className="flex flex-col items-center gap-6 py-12 flex-1">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={cn(
                  "text-xl font-medium transition-colors",
                  isActive(link.href)
                    ? "text-primary border-b-2 border-primary pb-1"
                    : "text-on-surface-variant hover:text-on-surface",
                )}
              >
                {link.label}
              </Link>
            ))}

            <div className="mt-6 pt-6 border-t border-border w-48 flex justify-center">
              <Link
                to="/chat"
                className="inline-flex items-center gap-1.5 bg-primary text-primary-foreground font-semibold text-base px-8 py-3 rounded-lg hover:bg-primary/90 transition-colors shadow-sm"
              >
                Launch Agent
                <span aria-hidden="true">&rarr;</span>
              </Link>
            </div>
          </nav>
        </div>
      )}
    </>
  );
}
